import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "../../components/ui/dialog";
import { UserPlus, Dice5, Trash2, Copy, Check, Pencil } from "lucide-react";
import { PLATFORM_STAFF_ROLES, generatePassword } from "./utils";
import EditStaffDialog from "./EditStaffDialog";
import { useConfirm } from "../../components/ConfirmProvider";

export default function PlatformStaffTab({ isSuperAdmin }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editS, setEditS] = useState(null);
  const confirm = useConfirm();

  const load = async () => {
    const { data } = await api.get("/superadmin/platform-staff");
    setStaff(data.staff || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const del = async (u) => {
    const ok = await confirm({
      title: `Remove ${u.name}?`,
      message: `They'll lose access to the Platform Console immediately.`,
      confirmText: "Remove",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/superadmin/platform-staff/${u.user_id}`);
      toast.success("Removed");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[#7A7571]">People who work at your company and can access this platform console.</p>
        {isSuperAdmin && <AddStaffDialog onDone={load} />}
      </div>

      {loading && <p className="text-[#7A7571]">Loading…</p>}

      <div className="card-soft !p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F4EC] text-[#7A7571]">
            <tr>
              <th className="text-left px-6 py-4 uppercase text-xs tracking-widest font-bold">Name</th>
              <th className="text-left px-6 py-4 uppercase text-xs tracking-widest font-bold hidden sm:table-cell">Email</th>
              <th className="text-left px-6 py-4 uppercase text-xs tracking-widest font-bold">Role</th>
              <th className="text-right px-6 py-4 uppercase text-xs tracking-widest font-bold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {staff.length === 0 && !loading && (
              <tr><td colSpan={4} className="px-6 py-10 text-center text-[#7A7571]">No platform staff yet.</td></tr>
            )}
            {staff.map((u) => {
              const meta = PLATFORM_STAFF_ROLES.find((r) => r.key === u.role) || { label: u.role, hint: "" };
              return (
                <tr key={u.user_id} className="border-t border-[#EBE5D9]" data-testid={`platform-staff-${u.user_id}`}>
                  <td className="px-6 py-4 font-semibold text-[#2C2A29]">{u.name}</td>
                  <td className="px-6 py-4 text-[#7A7571] hidden sm:table-cell break-all">{u.email}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="badge badge-neutral">{meta.label}</span>
                      <span className="text-xs text-[#7A7571] hidden md:inline">{meta.hint}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="inline-flex items-center gap-1">
                      {isSuperAdmin && (
                        <button onClick={() => setEditS(u)} data-testid={`platform-staff-edit-${u.user_id}`}
                          className="btn-secondary text-sm !py-1.5 !px-3 inline-flex items-center gap-1">
                          <Pencil size={12} /> Edit
                        </button>
                      )}
                      {isSuperAdmin && (
                        <button onClick={() => del(u)} data-testid={`platform-staff-delete-${u.user_id}`}
                          className="btn-ghost !p-2 text-[#9F1239]" title="Delete"><Trash2 size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <EditStaffDialog open={!!editS} onOpenChange={(v) => !v && setEditS(null)}
        staff={editS} onDone={load} canChangeRole={isSuperAdmin} />
    </div>
  );
}

function AddStaffDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "platform_staff" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [copied, setCopied] = useState(false);

  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post("/superadmin/platform-staff", {
        ...f, email: f.email.trim().toLowerCase(),
      });
      setDone({ email: f.email.trim().toLowerCase(), password: f.password, name: f.name });
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const close = () => {
    setOpen(false); setDone(null); setCopied(false);
    setF({ name: "", email: "", password: "", role: "platform_staff" });
  };

  const copyCreds = async () => {
    try {
      await navigator.clipboard.writeText(`OrkaiFlow Platform — sign-in\nEmail: ${done.email}\nPassword: ${done.password}`);
      setCopied(true); toast.success("Copied"); setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Copy failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else setOpen(true); }}>
      <DialogTrigger asChild>
        <button data-testid="add-staff-btn" className="btn-primary inline-flex items-center gap-2">
          <UserPlus size={16} /> Add staff
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Add platform staff</DialogTitle>
          <DialogDescription>Create a login for someone on your team.</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="space-y-4">
            <div className="card-soft !bg-[#F7F4EC] !p-5">
              <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2">Share with {done.name}</div>
              <div className="text-xs text-[#7A7571]">Email</div>
              <div className="font-mono text-sm mb-3 break-all">{done.email}</div>
              <div className="text-xs text-[#7A7571]">Password</div>
              <div className="font-mono text-sm">{done.password}</div>
              <button onClick={copyCreds} data-testid="add-staff-copy" className="btn-secondary text-sm !py-2 !px-3 mt-4 inline-flex items-center gap-2">
                {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy credentials</>}
              </button>
            </div>
            <button onClick={close} data-testid="add-staff-close" className="btn-primary w-full">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <Field label="Name" testId="staff-name" value={f.name} onChange={(v) => upd("name", v)} placeholder="Alex Chen" />
            <Field label="Email" testId="staff-email" type="email" value={f.email} onChange={(v) => upd("email", v)} placeholder="alex@yourdomain.com" />
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">Temporary password</label>
              <div className="flex gap-2">
                <input required data-testid="staff-password" type="text" className="input-soft font-mono flex-1"
                  value={f.password} onChange={(e) => upd("password", e.target.value)} placeholder="At least 6 characters" />
                <button type="button" onClick={() => upd("password", generatePassword(12))} data-testid="staff-genpw"
                  className="btn-secondary !py-2 !px-3 inline-flex items-center gap-1 flex-shrink-0"><Dice5 size={16} /></button>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">Role</label>
              <div className="space-y-2">
                {PLATFORM_STAFF_ROLES.map((r) => (
                  <button key={r.key} type="button" onClick={() => upd("role", r.key)}
                    data-testid={`staff-role-${r.key}`}
                    className={`w-full text-left px-4 py-3 rounded-2xl border transition-all ${f.role === r.key ? "border-[#E07A5F] bg-[#FFF6F1]" : "border-[#EBE5D9] bg-white hover:border-[#E07A5F]/40"}`}>
                    <div className="font-semibold text-[#2C2A29]">{r.label}</div>
                    <div className="text-xs text-[#7A7571]">{r.hint}</div>
                  </button>
                ))}
              </div>
            </div>
            <button data-testid="staff-submit" disabled={busy} className="btn-primary w-full">
              {busy ? "Creating…" : "Create staff account"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, testId, value, onChange, type = "text", placeholder }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">{label}</label>
      <input data-testid={testId} required type={type} value={value}
        onChange={(e) => onChange(e.target.value)} className="input-soft" placeholder={placeholder} />
    </div>
  );
}
